// Web Push subscription utility (iter246).
// Registers /sw-push.js, requests Notification permission, subscribes to
// PushManager with the backend-served VAPID public key, and POSTs the
// PushSubscription to /api/push/subscribe. Also handles unsubscribe on
// logout so notifications remain STRICTLY user/session-specific.

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const SW_URL = '/sw-push.js';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = typeof window !== 'undefined' ? window.atob(base64) : atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

function isSupported() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

async function getRegistration() {
  const existing = await navigator.serviceWorker.getRegistration(SW_URL);
  if (existing) return existing;
  return navigator.serviceWorker.register(SW_URL);
}

function authHeader(token) {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function fetchPublicKey(token) {
  // Prefer the compile-time env var so we don't waste a network call.
  const envKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
  if (envKey) return envKey;
  try {
    const res = await fetch(`${BACKEND}/api/push/public-key`, { headers: authHeader(token) });
    if (!res.ok) return null;
    const data = await res.json();
    return data?.key || null;
  } catch (_) {
    return null;
  }
}

export async function subscribeToPush({ token, sessionId } = {}) {
  if (!isSupported()) return { ok: false, reason: 'unsupported' };
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return { ok: false, reason: 'permission_' + permission };
  const publicKey = await fetchPublicKey(token);
  if (!publicKey) return { ok: false, reason: 'no_public_key' };
  const reg = await getRegistration();
  await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }
  const payload = sub.toJSON();
  try {
    const res = await fetch(`${BACKEND}/api/push/subscribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeader(token) },
      body: JSON.stringify({
        endpoint: payload.endpoint,
        keys: payload.keys,
        user_agent: navigator.userAgent,
        session_id: sessionId || '',
      }),
    });
    if (!res.ok) return { ok: false, reason: 'server_' + res.status };
    return { ok: true, endpoint: payload.endpoint };
  } catch (e) {
    return { ok: false, reason: 'network' };
  }
}

// Called on logout — invalidates the browser push subscription AND
// removes the row from the backend so the previous user's notifications
// cannot leak to a new session on the same device.
export async function unsubscribeFromPush({ token } = {}) {
  if (!isSupported()) return { ok: false, reason: 'unsupported' };
  try {
    const reg = await navigator.serviceWorker.getRegistration(SW_URL);
    if (!reg) return { ok: true, reason: 'no_registration' };
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return { ok: true, reason: 'no_subscription' };
    const { endpoint } = sub.toJSON();
    try { await sub.unsubscribe(); } catch (_) { /* ignore */ }
    if (endpoint) {
      try {
        await fetch(`${BACKEND}/api/push/unsubscribe`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeader(token) },
          body: JSON.stringify({ endpoint }),
        });
      } catch (_) { /* silent */ }
    }
    return { ok: true };
  } catch (_) {
    return { ok: false, reason: 'error' };
  }
}
